import React, { Suspense, useContext, useEffect, useState } from 'react';
import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { GlobalContext } from './lib/GlobalVar';
import { ConsultaPermissoes } from './lib/api';
import { ehRestrito } from './lib/acessoTela';
import { init as sfxInit } from './lib/sfx';

import Login from './components/Login';
import ChatDireto from './components/ChatDireto';
import HomePage from './components/HomePage';
import Dashboard from './components/Dashboard';
import Crontab from './components/Crontab';
import Internas from './components/Internas';
import TelaInterna from './components/TelaInterna';
import { PersonalizadoRota } from './personalizados/AbasPersonalizadas';
import Arquivos from './components/Arquivos';
import Logs from './components/Logs';
import Alertas from './components/Alertas';
import Configuracoes from './components/Configuracoes';
import TILogins from './components/TI/Logins';
import TIPermissoes from './components/TI/Permissoes';
import TIPermissoesMemoria from './components/TI/PermissoesMemoria';
import TISessoesMemoria from './components/TI/SessoesMemoria';
import TIBancos from './components/TI/Bancos';
import TIConstrutor from './components/TI/Construtor';
import TITelegram from './components/TI/Telegram';
import TIMonitor from './components/TI/Monitor';
import RedirecionaParaPagina from './personalizados/AreaVendedora/RedirecionaParaPagina';
// Avisos do navegador do Calendario Marketing Tarefas (tarefas do dia), em qualquer pagina: so trabalha no navegador de quem ligou os
// avisos na tela do calendario; o calculo e baixado sob demanda.
import AvisosCalendarioMkt from './personalizados/CalendarioMktTarefas/AvisosCalendarioMkt';

// Tutoriais em video + manual (pagina /tutoriais: EXIGE login do Central desde 26/10/2026; o servidor confere de novo). Carrega sob
// demanda: quem nunca abre a pagina nao baixa o codigo dela.
const Tutoriais = React.lazy(() => import('./components/Tutoriais/Tutoriais'));

// Area da Vendedora FORA do shell do Central (como /tutoriais): pagina propria, so logada; o servidor confere de novo (podeVer + escopo).
const PaginaVendedora = React.lazy(() => import('./personalizados/AreaVendedora/PaginaVendedora'));

// Redireciona para o login PRESERVANDO a página tentada (?next=...), p/ voltar a ela após entrar.
function RedirLogin() {
    const loc = useLocation();
    const alvo = loc.pathname + (loc.search || '');
    const next = (alvo && alvo !== '/') ? ('?next=' + encodeURIComponent(alvo)) : '';
    return <Navigate to={'/' + next} replace />;
}

function Private({ children }) {
    const { user } = useContext(GlobalContext);
    if (!user) return <RedirLogin />;
    return children;
}

// [bug7/iter5] Cache module-scope da matriz: evita um fetch por navegacao.
// Invalidado em logout (clearPermissaoCache) e por Permissoes.jsx via window.dispatchEvent.
let _matrizCache = null;
let _matrizPromise = null;
function loadMatriz() {
    if (_matrizCache) return Promise.resolve(_matrizCache);
    if (_matrizPromise) return _matrizPromise;
    _matrizPromise = ConsultaPermissoes({}).then(r => {
        _matrizCache = (r && !r.Error && r.matriz) ? r.matriz : {};
        _matrizPromise = null;
        return _matrizCache;
    }).catch(() => {
        _matrizPromise = null;
        return {};
    });
    return _matrizPromise;
}
export function clearPermissaoCache() {
    _matrizCache = null;
    _matrizPromise = null;
}
if (typeof window !== 'undefined') {
    window.addEventListener('centralbabita:permissoes-atualizadas', clearPermissaoCache);
}

function Negado() {
    return (
        <div className="page">
            <div className="page-head">
                <div>
                    <div className="eyebrow"><span className="num">!</span> Acesso negado</div>
                    <h1 className="page-title">Sem permissão</h1>
                    <p className="page-sub">Esta tela não está liberada para o seu setor. Procure o TI se precisar de acesso.</p>
                </div>
            </div>
        </div>
    );
}

// PermissionGate: bloqueia o render se a matriz disser nao.
// - Administrador: sempre permite
// - Telas TI/*: somente setor === 'TI'
// - Demais: consulta matriz[user.setor][screenId] (default: liberado)
function PermissionGate({ screenId, children }) {
    const { user } = useContext(GlobalContext);
    const [estado, setEstado] = useState(_matrizCache ? 'pronto' : 'loading');

    useEffect(() => {
        if (!user) return;
        if (_matrizCache) { setEstado('pronto'); return; }
        let alive = true;
        loadMatriz().then(() => { if (alive) setEstado('pronto'); });
        return () => { alive = false; };
    }, [user]);

    if (!user) return <RedirLogin />;
    if (estado === 'loading') return <div className="page muted" style={{ padding: 24 }}>Verificando permissão…</div>;

    if (user.cargo === 'Administrador') return children;
    // conta restrita: so Paginas Internas (onde estao as paginas liberadas a ela); o resto volta para la
    if (ehRestrito(user) && screenId !== 'Internas') return <Navigate to="/Internas" replace />;
    if (screenId.startsWith('TI/')) {
        return user.setor === 'TI' ? children : <Negado/>;
    }
    const setorBlock = (_matrizCache || {})[user.setor];
    if (setorBlock && setorBlock[screenId] === false) return <Negado/>;
    return children;
}

function App() {
    useEffect(() => { sfxInit(); }, []);
    return (
        <>
        <AvisosCalendarioMkt />
        <Routes>
            <Route path="/" element={<Login />} />
            {/* TAREFA 2: URL dedicada do chat (login-gated, fora do shell de menu). */}
            <Route path="/perguntar" element={<ChatDireto />} />
            <Route path="/cockpit" element={<ChatDireto />} />   {/* nome novo da pagina (03/09/2026); /perguntar continua valendo */}
            {/* /revendedora consolidado: redireciona para a landing unica /perguntar (modo revendedora). */}
            <Route path="/revendedora" element={<Navigate to="/perguntar?modo=revendedora" replace />} />
            {/* Tutoriais em video + manual para as vendedoras: SO LOGADO (<Private> leva ao login com ?next= e a pessoa volta para a
                pagina que queria), mas fora do shell de menu (HomePage). Uma rota so (/tutoriais, /tutoriais/grupo/:id e
                /tutoriais/:id) — o resto e lido da URL dentro da pagina. */}
            <Route path="/tutoriais/*" element={
                <Private>
                    <Suspense fallback={<div className="page muted" style={{ padding: 24 }}>Carregando…</div>}>
                        <Tutoriais />
                    </Suspense>
                </Private>
            } />
            {/* Area da Vendedora: pagina propria, fora do menu do Central (como o Cockpit e os Tutoriais), so logada. O endereco antigo
                (/personalizados/area-vendedora, da entrada de Paginas Internas) leva para ela e mantem a query (?vendedora=...). */}
            <Route path="/vendedora" element={
                <Private>
                    <Suspense fallback={<div className="page muted" style={{ padding: 24 }}>Carregando…</div>}>
                        <PaginaVendedora />
                    </Suspense>
                </Private>
            } />
            <Route path="/personalizados/area-vendedora" element={<RedirecionaParaPagina />} />
            <Route path="/" element={<Private><HomePage /></Private>}>
                <Route path="/Dashboard"     element={<PermissionGate screenId="Dashboard"><Dashboard /></PermissionGate>} />
                <Route path="/Crontab"       element={<PermissionGate screenId="Crontab"><Crontab /></PermissionGate>} />
                <Route path="/Internas"      element={<PermissionGate screenId="Internas"><Internas /></PermissionGate>} />
                <Route path="/internas/:slug" element={<PermissionGate screenId="Internas"><TelaInterna /></PermissionGate>} />
                <Route path="/personalizados/:slug" element={<PermissionGate screenId="Internas"><PersonalizadoRota /></PermissionGate>} />
                <Route path="/Arquivos"      element={<PermissionGate screenId="Arquivos"><Arquivos /></PermissionGate>} />
                <Route path="/Logs"          element={<PermissionGate screenId="Logs"><Logs /></PermissionGate>} />
                <Route path="/Alertas"       element={<PermissionGate screenId="Alertas"><Alertas /></PermissionGate>} />
                <Route path="/Configuracoes" element={<PermissionGate screenId="Configuracoes"><Configuracoes /></PermissionGate>} />
                <Route path="/TI/Logins"     element={<PermissionGate screenId="TI/Logins"><TILogins /></PermissionGate>} />
                <Route path="/TI/Permissoes" element={<PermissionGate screenId="TI/Permissoes"><TIPermissoes /></PermissionGate>} />
                <Route path="/TI/PermissoesMemoria" element={<PermissionGate screenId="TI/PermissoesMemoria"><TIPermissoesMemoria /></PermissionGate>} />
                <Route path="/TI/SessoesMemoria" element={<PermissionGate screenId="TI/SessoesMemoria"><TISessoesMemoria /></PermissionGate>} />
                <Route path="/TI/Bancos"     element={<PermissionGate screenId="TI/Bancos"><TIBancos /></PermissionGate>} />
                <Route path="/TI/Construtor" element={<PermissionGate screenId="TI/Construtor"><TIConstrutor /></PermissionGate>} />
                <Route path="/TI/Telegram"   element={<PermissionGate screenId="TI/Telegram"><TITelegram /></PermissionGate>} />
                <Route path="/TI/Monitor"    element={<PermissionGate screenId="TI/Monitor"><TIMonitor /></PermissionGate>} />
            </Route>
            <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
        </>
    );
}

export default App;
